/**
 * Safe Storage Service (localStorage wrapper)
 * SSR-safe + JSON support
 * Supports both individual keys and object-based storage
 */

const isBrowser = typeof window !== 'undefined';
const APP_STORAGE_KEY = 'appViewPreferences';

export const BrowserStorageService = {
    setItem(key, value) {
        if (!isBrowser) return;

        try {
            const storedValue =
                typeof value === 'string' ? value : JSON.stringify(value);
            window.localStorage.setItem(key, storedValue);
        } catch (error) {
            console.error(`StorageService setItem error for key "${key}"`, error);
        }
    },

    getItem(key, defaultValue = null) {
        if (!isBrowser) return defaultValue;

        try {
            const value = window.localStorage.getItem(key);
            if (value === null) return defaultValue;

            try {
                return JSON.parse(value);
            } catch {
                return value;
            }
        } catch (error) {
            console.error(`StorageService getItem error for key "${key}"`, error);
            return defaultValue;
        }
    },

    removeItem(key) {
        if (!isBrowser) return;
        window.localStorage.removeItem(key);
    },

    /**
     * Set multiple view preferences as a single object
     * Stores all preferences under APP_STORAGE_KEY
     * @param {Object} preferences - Object with key-value pairs of preferences
     */
    setViewPreferences(preferences = {}) {
        if (!isBrowser) return;

        try {
            const existingPreferences = this.getViewPreferences();
            const updatedPreferences = {
                ...existingPreferences,
                ...preferences
            };
            window.localStorage.setItem(APP_STORAGE_KEY, JSON.stringify(updatedPreferences));
        } catch (error) {
            console.error('StorageService setViewPreferences error', error);
        }
    },

    /**
     * Get all view preferences as an object
     * @param {Object} defaultValue - Default preferences if none exist
     * @returns {Object} All stored view preferences
     */
    getViewPreferences(defaultValue = {}) {
        if (!isBrowser) return defaultValue;

        try {
            const value = window.localStorage.getItem(APP_STORAGE_KEY);
            if (value === null) return defaultValue;

            try {
                return JSON.parse(value);
            } catch {
                return defaultValue;
            }
        } catch (error) {
            console.error('StorageService getViewPreferences error', error);
            return defaultValue;
        }
    },

    /**
     * Clear all application view preferences
     */
    clearViewPreferences() {
        if (!isBrowser) return;

        try {
            window.localStorage.removeItem(APP_STORAGE_KEY);
        } catch (error) {
            console.error('StorageService clearViewPreferences error', error);
        }
    },

    clear() {
        if (!isBrowser) return;
        window.localStorage.clear();
    }
};
